import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Pin, AlertTriangle, Info, Megaphone, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const IMPORTANCE_MAP = {
    urgent: {
        label: "Pilne",
        cls: "bg-red-50 text-red-700 border-red-200",
        Icon: AlertTriangle,
    },
    important: {
        label: "Ważne",
        cls: "bg-amber-50 text-amber-800 border-amber-200",
        Icon: Megaphone,
    },
    normal: {
        label: "Informacja",
        cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
        Icon: Info,
    },
};

const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
};

const truncate = (text, max = 180) => {
    const t = stripHtml(text).trim();
    if (t.length <= max) return t;
    return t.slice(0, max).trimEnd() + "…";
};

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" });
    } catch { return ""; }
};

export const Komunikaty = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visible, setVisible] = useState(4);

    useEffect(() => {
        api.get("/announcements")
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <section id="komunikaty" data-testid="komunikaty-section" className="py-24 md:py-32 bg-grain">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl mb-16"
                >
                    <p className="text-[#D4A537] uppercase tracking-[0.25em] text-xs font-semibold mb-4">Aktualne ogłoszenia</p>
                    <h2 className="font-serif text-4xl md:text-5xl text-[#092E22] leading-tight mb-4">
                        Komunikaty wspólnoty
                    </h2>
                    <div className="divider-gold w-24 mb-6" />
                    <p className="text-gray-600 text-base md:text-lg">
                        Najnowsze ogłoszenia administratora i zarządu wspólnoty. Ważne informacje są przypięte na górze.
                    </p>
                </motion.div>

                {loading && <p className="text-gray-500" data-testid="komunikaty-loading">Wczytywanie...</p>}

                {!loading && items.length === 0 && (
                    <p className="text-gray-500" data-testid="komunikaty-empty">Brak komunikatów do wyświetlenia.</p>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                    {items.slice(0, visible).map((it, idx) => {
                        const meta = IMPORTANCE_MAP[it.importance] || IMPORTANCE_MAP.normal;
                        const Icon = meta.Icon;
                        return (
                            <motion.article
                                key={it.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: idx * 0.08 }}
                                data-testid={`komunikat-card-${it.id}`}
                                className={`relative rounded-3xl p-7 md:p-8 bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${
                                    it.pinned ? "ring-2 ring-[#D4A537]/40" : ""
                                }`}
                            >
                                {it.pinned && (
                                    <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#D4A537] text-[#092E22] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                                        <Pin size={11} /> Przypięte
                                    </div>
                                )}
                                <div className="flex items-start justify-between mb-4 gap-3">
                                    <Badge className={`${meta.cls} border rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1.5`}>
                                        <Icon size={12} /> {meta.label}
                                    </Badge>
                                    <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(it.created_at)}</span>
                                </div>
                                <h3 className="font-serif text-2xl text-[#092E22] mb-3 leading-snug">{it.title}</h3>
                                <p className="text-gray-600 leading-relaxed text-sm md:text-base mb-4">
                                    {truncate(it.content, 200)}
                                </p>
                                <Link
                                    to={`/komunikat/${it.slug || it.id}`}
                                    data-testid={`komunikat-read-more-${it.id}`}
                                    className="inline-flex items-center gap-1.5 text-[#0D4F3C] hover:text-[#D4A537] text-sm font-medium transition-colors"
                                >
                                    Czytaj więcej <ArrowRight size={14} />
                                </Link>
                            </motion.article>
                        );
                    })}
                </div>

                {items.length > visible && (
                    <div className="text-center mt-10">
                        <button
                            onClick={() => setVisible((v) => v + 4)}
                            data-testid="komunikaty-load-more"
                            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white text-sm font-medium transition-all hover:-translate-y-0.5"
                        >
                            Pokaż więcej komunikatów ({items.length - visible})
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default Komunikaty;
